import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { message } from 'antd'
import {
  useCloseAppOnboardingNotification,
  useClickAppOnboardingNotification,
  useGetAppOnboardingNotification,
} from 'utils/queries/appOnboardingNotification'
import { usePaymentConnectActivateKledoMutation } from 'utils/queries/financePaymentConnectSetting'
import AppOnboardingNotification from 'components/apps/FinanceInvoices/AppOnboardingNotification'
import PaymentGatewayDetailModal from 'components/apps/FinanceInvoices/AppOnboardingNotification/PaymentGatewayDetailModal'
import PaymentGatewayDismissWarningModal from 'components/apps/FinanceInvoices/AppOnboardingNotification/PaymentGatewayDismissWarningModal'
import PaymentGatewayActivationSuccessModal from 'components/apps/FinanceInvoices/AppOnboardingNotification/PaymentGatewayActivationSuccessModal'
import {
  loadedInitSelector,
  locationSelector,
  optionsSelector,
  transTotalSelector,
  closedAppOnboardingNotificationIdsSelector,
  paymentConnectProvidersSelector,
} from 'app-redux/selectors'
import { closeAppOnboardingNotification } from 'app-redux/initSlice'
import { setPaymentConnectProviders } from 'app-redux/finance/actions'
import { editOptionsRedux } from 'app-redux/options/actions'
import { first, isEmpty } from 'lodash-es'
import { useQueryClient } from 'react-query'
import { decodeUriWithAtob } from 'utils/helper'
import useShowColumns from 'utils/hooks/useShowColumns'
import { useIntl } from 'react-intl'
import { buildInvoiceListPayload, parseFiltersToParams } from './invoiceListParams'
import SendInvoiceModal from '@/components/apps/FinanceInvoices/FinanceInvoicesTable/SendInvoiceModal'
import {
  getFinanceInvoiceEmailTemplate,
  getFinanceInvoiceWaTemplate,
  sendFinanceInvoiceEmail,
} from '@/utils/apis'
import useNavigation from '@/utils/hooks/useNavigation'
import { transType } from '@/utils/static'
import { useMenuTitle } from '@/utils/hooks'

const SHOW_ON_PAGE = {
  invoice: [/^\/sales\/invoices\/?$/, /^\/sales\/invoices\/detail\/\d+$/],
}

const isPathAvailable = (showOnPage, pathname) => {
  const patterns = SHOW_ON_PAGE[showOnPage]
  if (!patterns) {
    return false
  }
  return patterns.some((pattern) => pattern.test(pathname))
}

const getInvoiceDataFromCache = (
  queryClient,
  pathname,
  invoiceIdFromParams,
  listPayload = null,
) => {
  if (invoiceIdFromParams) {
    const queriesData = queryClient.getQueriesData(['invoice', invoiceIdFromParams])
    const cachedData = first(queriesData)?.[1]
    return cachedData || null
  }

  if (/^\/sales\/invoices\/?$/.test(pathname)) {
    if (listPayload) {
      const cachedData = queryClient.getQueryData(['invoice', listPayload])
      if (cachedData?.data) {
        return first(cachedData.data) || null
      }
    }
    const queriesData = queryClient.getQueriesData(['invoice'])
    const listQuery = queriesData.find(([key, data]) => {
      const isListQuery = key.length === 2 && Array.isArray(data?.data)
      return isListQuery
    })
    const listData = listQuery?.[1]
    return first(listData?.data) || null
  }

  return null
}

const DETAIL_PATH_REGEX = /^\/sales\/invoices\/detail\/(\d+)$/

const KLEDO_PROVIDER_ID = 4

const isPaymentGatewayNotification = (notifType) =>
  notifType?.template === 'blue_message' || notifType?.name === 'Payment Gateway'

const AppOnboardingNotificationContainer = () => {
  const queryClient = useQueryClient()
  const { formatMessage } = useIntl()

  const { onNavigate } = useNavigation()

  const dispatch = useDispatch()
  const location = useSelector(locationSelector)
  const loadedInit = useSelector(loadedInitSelector)
  const options = useSelector(optionsSelector)
  const paymentConnectProviders = useSelector(paymentConnectProvidersSelector)
  const transTotal = useSelector((state) => transTotalSelector(state, transType.INVOICE))
  const closedNotificationIds = useSelector(closedAppOnboardingNotificationIdsSelector)
  const invoicesTranslation = useMenuTitle({ name: 'invoices' })

  const pathName = location?.pathname || ''
  const filters = useMemo(() => decodeUriWithAtob(location?.search || ''), [location?.search])
  const { params, tableParams, transationDateParams } = useMemo(
    () => parseFiltersToParams(filters),
    [filters],
  )
  const showColumns = useShowColumns({
    name:
      params?.status_id === 'retur'
        ? 'show_columns_finance_invoice_returns'
        : 'show_columns_finance_invoices',
  })
  const invoiceListPayload = useMemo(() => {
    if (
      !/^\/sales\/invoices\/?$/.test(pathName) ||
      params?.status_id === 'recurring' ||
      params?.status_id === 'retur'
    ) {
      return null
    }
    return buildInvoiceListPayload({
      params,
      tableParams,
      transationDateParams,
      search: filters?.search || '',
      options,
      transTotal,
      showColumns: showColumns?.toStringColumns || '',
    })
  }, [
    pathName,
    params,
    tableParams,
    transationDateParams,
    filters?.search,
    options,
    transTotal,
    showColumns?.toStringColumns,
  ])

  const invoiceId = useMemo(() => {
    const match = pathName.match(DETAIL_PATH_REGEX)
    return match?.[1] || null
  }, [pathName])
  const closeMutation = useCloseAppOnboardingNotification()
  const clickMutation = useClickAppOnboardingNotification()
  const activateKledoMutation = usePaymentConnectActivateKledoMutation()

  const [sendWaModal, setSendWaModal] = useState(false)
  const [notificationData, setNotificationData] = useState(null)
  const [selectedData, setSelectedData] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [dismissWarningOpen, setDismissWarningOpen] = useState(false)
  const [paymentGatewaySuccessModalOpen, setPaymentGatewaySuccessModalOpen] = useState(false)

  const handleCloseWaModal = useCallback(() => {
    setSendWaModal(false)
    setSelectedData(null)
  }, [])

  const { data } = useGetAppOnboardingNotification({ enabled: loadedInit })
  const notificationPayload = useMemo(() => data?.data || data || null, [data])

  useEffect(() => {
    if (notificationPayload && !isEmpty(notificationPayload)) {
      const showOnPage = notificationPayload?.notif_type?.show_on_page
      const notificationId = notificationPayload?.id

      const isClosed = notificationId && closedNotificationIds.includes(notificationId)

      if (showOnPage && isPathAvailable(showOnPage, pathName) && !isClosed) {
        setNotificationData(notificationPayload)
      } else {
        setNotificationData(null)
      }
    }
  }, [notificationPayload, pathName, closedNotificationIds])

  const bannerVisible = useMemo(() => {
    if (isEmpty(notificationData)) {
      return false
    }
    const showOnPage = notificationData?.notif_type?.show_on_page
    return showOnPage ? isPathAvailable(showOnPage, pathName) : false
  }, [notificationData, pathName])

  const shouldRender = useMemo(
    () => bannerVisible || paymentGatewaySuccessModalOpen,
    [bannerVisible, paymentGatewaySuccessModalOpen],
  )

  const isPaymentGateway = useMemo(
    () => isPaymentGatewayNotification(notificationData?.notif_type),
    [notificationData],
  )

  useEffect(() => {
    if (!notificationData) {
      setDetailModalOpen(false)
      setDismissWarningOpen(false)
    }
  }, [notificationData])

  const goToEmailTemplateInvoice = useCallback(
    () => onNavigate(`/settings/email-template?type=${transType.INVOICE}`),
    [onNavigate],
  )

  const handleClose = useCallback(
    (id) => {
      setNotificationData(null)
      if (id) {
        dispatch(closeAppOnboardingNotification(id))
      }
      closeMutation.mutate(id)
    },
    [closeMutation, dispatch],
  )

  const handleClosePaymentGatewaySuccessModal = useCallback(() => {
    setPaymentGatewaySuccessModalOpen(false)
  }, [])

  const activatePaymentConnectKledo = useCallback(
    (notificationId) => {
      if (!notificationId) {
        return
      }
      activateKledoMutation.mutate(
        { is_active: true },
        {
          onSuccess: (response) => {
            if (!response?.data?.success) {
              message.error(
                response?.data?.message ||
                  formatMessage({ id: 'appOnboarding.paymentGateway.activate_error_fallback' }),
              )
              return
            }

            setDetailModalOpen(false)
            setDismissWarningOpen(false)

            const d = response?.data?.data
            const providersList = paymentConnectProviders ?? []
            if (d?.provider_id != null && typeof d.is_active !== 'undefined') {
              dispatch(
                setPaymentConnectProviders(
                  providersList.map((p) =>
                    p.id === d.provider_id ? { ...p, is_active: Boolean(d.is_active) } : p,
                  ),
                ),
              )
            } else {
              dispatch(
                setPaymentConnectProviders(
                  providersList.map((p) =>
                    p.id === KLEDO_PROVIDER_ID ? { ...p, is_active: true } : p,
                  ),
                ),
              )
            }

            const raw = options.payment_connect_default
            const hasDefault = raw != null && `${raw}` !== ''
            if (!hasDefault) {
              dispatch(editOptionsRedux({ payment_connect_default: KLEDO_PROVIDER_ID }))
            }

            clickMutation.mutate(notificationId)
            handleClose(notificationId)
            setPaymentGatewaySuccessModalOpen(true)
          },
          onError: (error) => {
            const msg = error?.response?.data?.message || error?.data?.message || error?.message
            message.error(
              msg || formatMessage({ id: 'appOnboarding.paymentGateway.activate_error_fallback' }),
            )
          },
        },
      )
    },
    [
      activateKledoMutation,
      clickMutation,
      dispatch,
      formatMessage,
      handleClose,
      options.payment_connect_default,
      paymentConnectProviders,
    ],
  )

  const handleBannerClose = useCallback(
    (id) => {
      if (isPaymentGateway) {
        setDismissWarningOpen(true)
        return
      }
      handleClose(id)
    },
    [handleClose, isPaymentGateway],
  )

  const handleDetailNo = useCallback(() => {
    setDetailModalOpen(false)
    setDismissWarningOpen(true)
  }, [])

  const handleDismissDecline = useCallback(() => {
    const id = notificationData?.id
    setDismissWarningOpen(false)
    if (id) {
      handleClose(id)
    }
  }, [handleClose, notificationData?.id])

  const handleClick = useCallback(
    (id) => {
      if (isPaymentGateway) {
        setDetailModalOpen(true)
        return
      }

      const templateType = notificationData?.notif_type?.template
      const notifName = notificationData?.notif_type?.name
      const isWebPosNotification = templateType === 'orange_message' || notifName === 'Web POS'

      if (isWebPosNotification) {
        clickMutation.mutate(id)
        return
      }

      const invoiceData = getInvoiceDataFromCache(
        queryClient,
        pathName,
        invoiceId,
        invoiceListPayload,
      )
      setSelectedData(invoiceData)
      if (invoiceData) {
        setSendWaModal(true)
      }
      clickMutation.mutate(id)
    },
    [
      clickMutation,
      invoiceId,
      invoiceListPayload,
      isPaymentGateway,
      notificationData,
      pathName,
      queryClient,
    ],
  )

  if (!shouldRender) {
    return null
  }

  return (
    <>
      {bannerVisible && (
        <AppOnboardingNotification
          data={notificationData || {}}
          onClose={isPaymentGateway ? handleBannerClose : handleClose}
          onClick={handleClick}
        />
      )}
      {detailModalOpen && (
        <PaymentGatewayDetailModal
          open={detailModalOpen}
          onCancel={handleDetailNo}
          onActivate={activatePaymentConnectKledo}
          notificationId={notificationData?.id}
          activateLoading={activateKledoMutation.isLoading}
        />
      )}
      {dismissWarningOpen && (
        <PaymentGatewayDismissWarningModal
          open={dismissWarningOpen}
          onCancel={handleDismissDecline}
          onActivate={activatePaymentConnectKledo}
          notificationId={notificationData?.id}
          activateLoading={activateKledoMutation.isLoading}
        />
      )}
      {paymentGatewaySuccessModalOpen && (
        <PaymentGatewayActivationSuccessModal
          open={paymentGatewaySuccessModalOpen}
          onClose={handleClosePaymentGatewaySuccessModal}
        />
      )}
      {sendWaModal && selectedData && (
        <SendInvoiceModal
          title={`${formatMessage({ id: 'button.send' })} ${invoicesTranslation} ${selectedData.ref_number || ''}`}
          open={sendWaModal}
          onClose={handleCloseWaModal}
          data={selectedData}
          isMass={false}
          goToEmailTemplate={goToEmailTemplateInvoice}
          getEmailTemplate={() => getFinanceInvoiceEmailTemplate(selectedData?.id)}
          emailTemplateKey={['emailTemplate', transType.INVOICE, selectedData?.id]}
          getWaTemplate={() => getFinanceInvoiceWaTemplate(selectedData?.id)}
          waTemplateKey={['waTemplate', transType.INVOICE]}
          sendEmail={sendFinanceInvoiceEmail}
          transTypeId={transType.INVOICE}
          withRelationAttachment={!!selectedData?.business_tran_id}
        />
      )}
    </>
  )
}

export default AppOnboardingNotificationContainer
